// richTextTiptap.js

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Services
import { subscribeToNode } from 'services/nodes/NodeReadService';

// Hooks
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// Utilities
import { RICH_TEXT_EMBEDDED_NODE_TYPE, RICH_TEXT_IMAGE_TYPE } from 'utilities/RichText';
import { parseHelpImageWidth } from './richTextImageUtils';

// Components
import GalleryViewNodeCard from 'views/gallery/cards/GalleryViewNodeCard';
import HelpImageView from './HelpImageView';
import PhotoCard from 'views/gallery/cards/PhotoCard';
import VideoCard from 'views/gallery/cards/VideoCard';

// Tiptap
import { Node } from '@tiptap/core';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

const EMPTY_FIELD_MAP = new Map();

const resolveEmptyFieldValue = () => '';
const formatEmptyFieldValue = () => '';

const buildSnapshotNode = (attrs) => {
    const nextAttrs = attrs && typeof attrs === 'object' ? attrs : {};

    return {
        key: String(nextAttrs.nodeKey || '').trim(),
        baseKey: String(nextAttrs.baseKey || '').trim(),
        type: String(nextAttrs.nodeType || 'NODE').trim() || 'NODE',
        title: String(nextAttrs.title || '').trim() || 'Untitled',
        subtitle: String(nextAttrs.subtitle || nextAttrs.description || '').trim(),
        coverStoragePath: String(nextAttrs.coverStoragePath || '').trim(),
        coverUrl: String(nextAttrs.coverUrl || '').trim(),
    };
};

const getEmbeddedNodeStatus = (node) => {
    if (!node?.key) return 'unavailable';
    return 'ready';
};

const useEmbeddedNodeData = (attrs) => {
    const snapshotNode = useMemo(() => buildSnapshotNode(attrs), [attrs]);

    // ----- STATE ----- //

    const [embeddedNodeState, setEmbeddedNodeState] = useState(() => ({
        node: snapshotNode.key ? snapshotNode : null,
        status: snapshotNode.key ? 'loading' : 'unavailable',
    }));

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!snapshotNode.key) {
            setEmbeddedNodeState({
                node: null,
                status: 'unavailable',
            });
            return undefined;
        }

        setEmbeddedNodeState({
            node: snapshotNode,
            status: 'loading',
        });

        return subscribeToNode(snapshotNode.key, (node) => {
            setEmbeddedNodeState({
                node: node || null,
                status: node ? getEmbeddedNodeStatus(node) : 'unavailable',
            });
        });
    }, [snapshotNode]);

    return embeddedNodeState;
};

/**
 * EmbeddedNodeView
 *
 * Renders an embedded node inside rich text content using existing card surfaces.
 *
 * @param {object} props - Tiptap node view props.
 *
 * @returns {ReactNode} The rendered embedded node card.
 */
const EmbeddedNodeView = React.memo((props) => {
    const { deleteNode, extension, node, selected } = props;

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- HOOKS ----- //

    const { openNode } = useNodeOpenIntent();

    // ----- MEMOS ----- //

    const attrs = useMemo(() => node?.attrs || {}, [node?.attrs]);
    const embeddedNodeState = useEmbeddedNodeData(attrs);
    const displayNode = embeddedNodeState.node || buildSnapshotNode(attrs);
    const typeKey = String(displayNode?.type || '').toUpperCase();
    const isReadOnly = extension?.options?.readOnly === true;

    // ----- HANDLERS ----- //

    /**
     * handleOpen
     *
     * Opens the related surface.
     *
     * @param {object} event - event.
     */
    const handleOpen = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!displayNode?.key) return;
        openNode(displayNode);
    };

    /**
     * handleRemove
     *
     * Handles remove.
     *
     * @param {object} event - event.
     */
    const handleRemove = (event) => {
        event.preventDefault();
        event.stopPropagation();
        deleteNode();
    };
    const renderCardBody = () => {
        if (embeddedNodeState.status === 'loading') {
            return (
                <div
                    className="rich-text-editor-embed-loading"
                    style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    Loading embedded item...
                </div>
            );
        }

        if (!displayNode?.key || embeddedNodeState.status === 'unavailable') {
            return (
                <div
                    className="rich-text-editor-embed-empty"
                    style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    Embedded item unavailable.
                </div>
            );
        }

        if (typeKey === 'PHOTO') {
            return <PhotoCard node={displayNode} />;
        }

        if (typeKey === 'VIDEO') {
            return <VideoCard node={displayNode} />;
        }

        return (
            <GalleryViewNodeCard
                cardFieldKeys={[]}
                formatFieldValue={formatEmptyFieldValue}
                node={displayNode}
                resolveFieldValue={resolveEmptyFieldValue}
                schemaFieldMap={EMPTY_FIELD_MAP}
                theme={theme}
            />
        );
    };


    // ----- RENDER ----- //

    return (
        <NodeViewWrapper className="rich-text-editor-embed-node">
            <div
                className="rich-text-editor-embed-card"
                style={{
                    backgroundColor: theme.backgroundMuted,
                    borderColor: selected ? theme.accent : theme.backgroundHover,
                }}
            >
                <div className="rich-text-editor-embed-card-body">
                    {renderCardBody()}
                </div>

                {!isReadOnly ? (
                    <div
                        className="rich-text-editor-embed-actions"
                        style={{
                            borderTopColor: theme.backgroundHover,
                        }}
                    >
                        <button
                            className="rich-text-editor-embed-action"
                            style={{
                                borderColor: theme.backgroundHover,
                                color: theme.foreground,
                            }}
                            type="button"
                            onClick={handleOpen}
                        >
                            Open
                        </button>
                        <button
                            className="rich-text-editor-embed-action"
                            style={{
                                borderColor: theme.backgroundHover,
                                color: theme.foreground,
                            }}
                            type="button"
                            onClick={handleRemove}
                        >
                            Remove
                        </button>
                    </div>
                ) : null}
            </div>
        </NodeViewWrapper>
    );
});

EmbeddedNodeView.displayName = 'EmbeddedNodeView';

export const createHelpImageExtension = ({ readOnly = false } = {}) => Node.create({
    name: RICH_TEXT_IMAGE_TYPE,
    group: 'block',
    atom: true,
    draggable: true,
    selectable: true,

    addOptions() {
        return {
            readOnly,
        };
    },

    addAttributes() {
        return {
            alt: { default: '' },
            src: { default: '' },
            storagePath: { default: '' },
            width: {
                default: null,
                parseHTML: (element) => parseHelpImageWidth(
                    element.getAttribute('width') || element.getAttribute('data-width')
                ),
                renderHTML: (attributes) => {
                    const width = parseHelpImageWidth(attributes.width);
                    if (!width) return {};
                    return { width: String(width) };
                },
            },
        };
    },

    parseHTML() {
        return [{ tag: 'img' }];
    },

    renderHTML({ HTMLAttributes }) {
        const width = parseHelpImageWidth(HTMLAttributes.width);
        return ['img', {
            alt: HTMLAttributes.alt || '',
            src: HTMLAttributes.src || '',
            'data-storage-path': HTMLAttributes.storagePath || '',
            ...(width ? { width: String(width) } : {}),
        }];
    },

    addNodeView() {
        return ReactNodeViewRenderer(HelpImageView);
    },
});

export const createEmbeddedNodeExtension = ({ readOnly = false } = {}) => Node.create({
    name: RICH_TEXT_EMBEDDED_NODE_TYPE,
    group: 'block',
    atom: true,
    draggable: true,
    selectable: true,

    addOptions() {
        return {
            readOnly,
        };
    },

    addAttributes() {
        return {
            coverStoragePath: { default: '' },
            coverUrl: { default: '' },
            subtitle: { default: '' },
            description: { default: '' },
            display: { default: 'card' },
            nodeKey: { default: '' },
            nodeType: { default: 'NODE' },
            title: { default: '' },
            baseKey: { default: '' },
        };
    },

    parseHTML() {
        return [{ tag: 'div[data-embedded-node]' }];
    },

    renderHTML({ HTMLAttributes }) {
        const {
            coverStoragePath,
            coverUrl,
            subtitle,
            description,
            display,
            nodeKey,
            nodeType,
            title,
            baseKey,
            ...restAttributes
        } = HTMLAttributes;

        return ['div', {
            ...restAttributes,
            'data-cover-storage-path': coverStoragePath || '',
            'data-cover-url': coverUrl || '',
            'data-subtitle': subtitle || description || '',
            'data-description': description || subtitle || '',
            'data-display': display || 'card',
            'data-embedded-node': 'true',
            'data-node-key': nodeKey || '',
            'data-node-type': nodeType || '',
            'data-title': title || '',
            'data-base-key': baseKey || '',
        }];
    },

    addNodeView() {
        return ReactNodeViewRenderer(EmbeddedNodeView);
    },
});

export function createRichTextExtensions({ placeholder = '', readOnly = false } = {}) {
    const extensions = [
        StarterKit.configure({
            heading: {
                levels: [1, 2, 3],
            },
        }),
        Underline,
        createEmbeddedNodeExtension({ readOnly }),
        createHelpImageExtension({ readOnly }),
    ];

    if (placeholder) {
        extensions.splice(2, 0, Placeholder.configure({
            placeholder,
        }));
    }

    return extensions;
}